import { useEffect, useState } from "react";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { Upload, Trash2, RotateCcw, Save, Sparkles, Eye, AlertTriangle, CheckCircle2, ImageIcon } from "lucide-react";

const TEMPLATES = [
  { key: "clinico",     label: "Clínico Profesional", desc: "Verde oscuro / crema · serio y confiable", colors: ["#1B4332", "#52796F", "#FAF8F4"] },
  { key: "moderno",     label: "Moderno Minimalista", desc: "Negro / blanco · estilo Linear/Notion",     colors: ["#0A0A0A", "#525252", "#FFFFFF"] },
  { key: "elegante",    label: "Elegante Boutique",   desc: "Burdeos / dorado · estética dental premium", colors: ["#7C2D12", "#B8860B", "#FBF7F0"] },
  { key: "pediatrico",  label: "Pediátrico Amigable", desc: "Azul cielo / amarillo · odontopediatría",   colors: ["#0EA5E9", "#FACC15", "#F0F9FF"] },
  { key: "tecnologico", label: "Tecnológico Futurista", desc: "Azul eléctrico / morado · CAD/CAM",       colors: ["#2563EB", "#7C3AED", "#F8FAFC"] },
  { key: "wellness",    label: "Natural Wellness",    desc: "Verde salvia / beige · biodontología",      colors: ["#5C7C5A", "#A47551", "#F7F4ED"] },
];

const FONT_TITLES = ["Inter", "Playfair Display", "Montserrat", "Poppins", "Lora", "DM Serif Display", "Space Grotesk"];
const FONT_BODIES = ["Inter", "Roboto", "Open Sans", "Lato", "Source Sans 3", "Nunito", "DM Sans"];

// WCAG contrast ratio (relative luminance approach)
function luminance(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return 0;
  const rs = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs[0] + 0.7152 * rs[1] + 0.0722 * rs[2];
}
function contrastRatio(a, b) {
  const la = luminance(a), lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function ColorRow({ label, value, onChange, contrastVs, testId }) {
  const ratio = contrastVs ? contrastRatio(value, contrastVs) : null;
  const lowContrast = ratio !== null && ratio < 4.5;
  return (
    <div className="grid grid-cols-12 items-center gap-3 py-2">
      <div className="col-span-5 text-sm font-medium">{label}</div>
      <div className="col-span-3 flex items-center gap-2">
        <input type="color" className="w-10 h-10 rounded-sm border border-border cursor-pointer"
          value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        <input className="input-flat !h-9 font-mono text-xs w-24" value={value}
          onChange={(e) => onChange(e.target.value)} maxLength={7} />
      </div>
      <div className="col-span-4 text-xs">
        {ratio !== null && (
          <div className={`flex items-center gap-1 ${lowContrast ? "text-amber-600" : "text-emerald-600"}`}>
            {lowContrast ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
            Contraste: {ratio.toFixed(2)}:1 {lowContrast ? "(bajo)" : "(OK)"}
          </div>
        )}
      </div>
    </div>
  );
}

function LogoSlot({ label, hint, value, slot, onUpload, onRemove }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen es demasiado grande. Máximo 2MB.");
      return;
    }
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await onUpload(slot, reader.result);
          toast.success("Logo actualizado");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Error subiendo logo");
        } finally {
          setBusy(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setBusy(false);
    }
    e.target.value = "";
  };
  return (
    <div className="card-flat p-3 flex items-center gap-3">
      <div className="w-20 h-20 rounded-sm border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
        {value ? (
          <img src={value} alt={label} className="max-w-full max-h-full object-contain" />
        ) : (
          <ImageIcon className="w-6 h-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold">{label}</div>
        <div className="text-[11px] text-muted-foreground mb-2">{hint}</div>
        <div className="flex items-center gap-2">
          <label className="px-2 py-1 rounded-sm border border-border bg-card hover:bg-muted text-[10px] uppercase tracking-widest font-bold cursor-pointer flex items-center gap-1">
            <Upload className="w-3 h-3" /> Subir
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={busy} data-testid={`upload-logo-${slot}`} />
          </label>
          {value && (
            <button type="button" onClick={() => onRemove(slot)}
              className="px-2 py-1 rounded-sm border border-border text-destructive hover:bg-destructive/10 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
              data-testid={`remove-logo-${slot}`}>
              <Trash2 className="w-3 h-3" /> Quitar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BrandingSection({ isAdmin }) {
  const { branding, updateBranding, applyTemplate, resetBranding, uploadLogo, removeLogo } = useBranding();
  const [form, setForm] = useState(branding);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(branding); }, [branding]);

  if (!isAdmin) {
    return (
      <div className="card-flat p-6 text-sm text-muted-foreground">
        Solo el administrador puede modificar la apariencia y marca de la clínica.
      </div>
    );
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateBranding(form);
      toast.success("Apariencia guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("¿Restablecer todo a los valores DentCloud por defecto? Los logos se eliminarán.")) return;
    try {
      await resetBranding();
      toast.success("Restablecido a DentCloud");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const applyTpl = async (tpl) => {
    try {
      await applyTemplate(tpl);
      toast.success(`Plantilla "${TEMPLATES.find(t => t.key === tpl).label}" aplicada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-8" data-testid="branding-section">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Apariencia y Marca</h2>
          <p className="text-sm text-muted-foreground mt-1">Personaliza tu instancia: logo, colores, tipografía y plantillas.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} className="px-3 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest flex items-center gap-2" data-testid="branding-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Restablecer
          </button>
          <button onClick={save} disabled={saving} className="btn-primary !h-10" data-testid="branding-save">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>

      {/* 1.4 Plantillas */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-display font-bold text-lg">Plantillas precargadas</h3>
        </div>
        <p className="text-xs text-muted-foreground">Aplica un tema completo (paleta + tipografía + estilo) con un click.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TEMPLATES.map((tpl) => {
            const active = branding.plantilla_aplicada === tpl.key;
            return (
              <button key={tpl.key} type="button" onClick={() => applyTpl(tpl.key)}
                className={`text-left p-4 rounded-sm border transition ${active ? "border-primary ring-2 ring-primary bg-primary/5" : "border-border hover:bg-muted"}`}
                data-testid={`template-${tpl.key}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  {tpl.colors.map((c, i) => (
                    <div key={i} className="w-5 h-5 rounded-sm border border-border" style={{ background: c }} />
                  ))}
                </div>
                <div className="text-sm font-bold">{tpl.label}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{tpl.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* 1.1 Identidad */}
      <section className="space-y-4">
        <h3 className="font-display font-bold text-lg">Identidad visual</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-2">Nombre comercial</label>
            <input className="input-flat" value={form.nombre_comercial || ""} onChange={(e) => setField("nombre_comercial", e.target.value)} data-testid="branding-name" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Eslogan</label>
            <input className="input-flat" value={form.eslogan || ""} onChange={(e) => setField("eslogan", e.target.value)} placeholder="Sonríe sin miedo" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">NIT</label>
            <input className="input-flat font-mono" value={form.nit || ""} onChange={(e) => setField("nit", e.target.value)} />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Teléfono</label>
            <input className="input-flat font-mono" value={form.telefono || ""} onChange={(e) => setField("telefono", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-2">Dirección</label>
            <input className="input-flat" value={form.direccion || ""} onChange={(e) => setField("direccion", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-2">Email contacto</label>
            <input className="input-flat" type="email" value={form.email_contacto || ""} onChange={(e) => setField("email_contacto", e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 pt-3">
          <LogoSlot label="Logo principal" hint="Header y login. PNG/SVG, 200×60px"
            value={branding.logo_principal_b64} slot="principal" onUpload={uploadLogo} onRemove={removeLogo} />
          <LogoSlot label="Favicon / cuadrado" hint="192×192px. Pestaña navegador."
            value={branding.logo_cuadrado_b64} slot="cuadrado" onUpload={uploadLogo} onRemove={removeLogo} />
          <LogoSlot label="Logo documentos" hint="Recibos/facturas. 600×180px"
            value={branding.logo_documentos_b64} slot="documentos" onUpload={uploadLogo} onRemove={removeLogo} />
        </div>
      </section>

      {/* 1.2 Paleta */}
      <section className="space-y-2">
        <h3 className="font-display font-bold text-lg">Paleta de colores</h3>
        <div className="card-flat p-4 divide-y divide-border">
          <ColorRow label="Color primario (botones, header)" value={form.color_primario} onChange={(v) => setField("color_primario", v)} contrastVs="#FFFFFF" testId="color-primario" />
          <ColorRow label="Color secundario (acentos)" value={form.color_secundario} onChange={(v) => setField("color_secundario", v)} contrastVs="#FFFFFF" testId="color-secundario" />
          <ColorRow label="Fondo (modo claro)" value={form.color_fondo_light} onChange={(v) => setField("color_fondo_light", v)} contrastVs={form.color_texto_principal} testId="color-fondo-light" />
          <ColorRow label="Fondo (modo oscuro)" value={form.color_fondo_dark} onChange={(v) => setField("color_fondo_dark", v)} testId="color-fondo-dark" />
          <ColorRow label="Éxito" value={form.color_exito} onChange={(v) => setField("color_exito", v)} contrastVs="#FFFFFF" />
          <ColorRow label="Advertencia" value={form.color_advertencia} onChange={(v) => setField("color_advertencia", v)} contrastVs="#FFFFFF" />
          <ColorRow label="Error" value={form.color_error} onChange={(v) => setField("color_error", v)} contrastVs="#FFFFFF" />
          <ColorRow label="Texto principal" value={form.color_texto_principal} onChange={(v) => setField("color_texto_principal", v)} contrastVs={form.color_fondo_light} />
          <ColorRow label="Texto secundario" value={form.color_texto_secundario} onChange={(v) => setField("color_texto_secundario", v)} contrastVs={form.color_fondo_light} />
        </div>
      </section>

      {/* 1.3 Tipografía */}
      <section className="space-y-3">
        <h3 className="font-display font-bold text-lg">Tipografía</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow block mb-2">Fuente para títulos</label>
            <select className="input-flat" value={form.fuente_titulos} onChange={(e) => setField("fuente_titulos", e.target.value)} data-testid="font-titles">
              {FONT_TITLES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <div className="mt-2 px-3 py-2 rounded-sm border border-border" style={{ fontFamily: `'${form.fuente_titulos}', sans-serif`, fontSize: "20px", fontWeight: 700 }}>
              Aa Bb Cc — Clínica DentCloud
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Fuente para cuerpo</label>
            <select className="input-flat" value={form.fuente_cuerpo} onChange={(e) => setField("fuente_cuerpo", e.target.value)} data-testid="font-body">
              {FONT_BODIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <div className="mt-2 px-3 py-2 rounded-sm border border-border" style={{ fontFamily: `'${form.fuente_cuerpo}', sans-serif`, fontSize: "14px" }}>
              Lorem ipsum dolor sit amet consectetur.
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Tamaño base</label>
            <select className="input-flat" value={form.tamano_base} onChange={(e) => setField("tamano_base", e.target.value)} data-testid="font-size">
              <option value="compact">Compacto (14px)</option>
              <option value="normal">Normal (16px)</option>
              <option value="comfort">Cómodo (18px)</option>
            </select>
          </div>
        </div>
      </section>

      {/* 1.5 Estilo de componentes */}
      <section className="space-y-3">
        <h3 className="font-display font-bold text-lg">Estilo de componentes</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="label-eyebrow block mb-2">Bordes redondeados</label>
            <select className="input-flat" value={form.border_radius} onChange={(e) => setField("border_radius", e.target.value)}>
              <option value="none">Cuadrado</option>
              <option value="subtle">Sutil (4px)</option>
              <option value="rounded">Redondeado (8px)</option>
              <option value="extra">Muy redondeado (16px)</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Sombras</label>
            <select className="input-flat" value={form.sombras_estilo} onChange={(e) => setField("sombras_estilo", e.target.value)}>
              <option value="none">Ninguna</option>
              <option value="subtle">Sutiles</option>
              <option value="bold">Marcadas</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Densidad</label>
            <select className="input-flat" value={form.densidad} onChange={(e) => setField("densidad", e.target.value)}>
              <option value="compact">Compacta</option>
              <option value="normal">Normal</option>
              <option value="spacious">Espaciada</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Animaciones</label>
            <select className="input-flat" value={form.animaciones_activas ? "1" : "0"} onChange={(e) => setField("animaciones_activas", e.target.value === "1")}>
              <option value="1">Activadas</option>
              <option value="0">Reducidas</option>
            </select>
          </div>
        </div>
      </section>

      {/* 1.6 Copys */}
      <section className="space-y-3">
        <h3 className="font-display font-bold text-lg">Textos personalizados</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-2">Mensaje de bienvenida (dashboard)</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.mensaje_bienvenida || ""} onChange={(e) => setField("mensaje_bienvenida", e.target.value)} placeholder="Bienvenido a tu día en la clínica" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Footer de recibos / PDFs</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.footer_recibos || ""} onChange={(e) => setField("footer_recibos", e.target.value)} placeholder="Gracias por confiar en nosotros." />
          </div>
        </div>
      </section>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <button onClick={save} disabled={saving} className="btn-primary !h-11" data-testid="branding-save-bottom">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
